/**
 * SES-010's story, in Calder & Wynn (fixture-calder.ts): which clients need
 * attention before month-end, answered across the firm's CRM (HubSpot), its
 * accounts (Xero) and its email (Outlook), with the client record the Twin
 * keeps across all three. `fixture-clients.test.ts` checks the answer
 * against the records and every printed value against the application's
 * formatters.
 */

import type { WorkspaceField } from "@product/lib/mlx-workspace-client";

import {
  CALDER_CHANNELS,
  CALDER_NOW,
  CALDER_TURN_TIMES,
  calderRecordHref,
  calderWorkspace,
  daysBetween,
  sessionsRunning,
  workflowField,
} from "./fixture-calder";

export const CLIENTS_NOW = CALDER_NOW;
export const CLIENTS_CHANNEL_ID = CALDER_CHANNELS.accounts.id;
export const CLIENTS_CHANNEL_NAME = CALDER_CHANNELS.accounts.name;
export const CLIENTS_DATA_SET_NAME = "Clients";

export const CLIENTS_WORKSPACE = calderWorkspace({
  sessionId: "ses_clients_attention",
  sessionTitle: "Clients needing attention",
});

/** The rail while the run works. */
export const CLIENTS_SESSIONS_RUNNING = sessionsRunning(CLIENTS_WORKSPACE);

export const CLIENTS_TURN_TIMES = CALDER_TURN_TIMES;

/* ------------------------------------------------------------------ */
/*  The clients                                                         */
/* ------------------------------------------------------------------ */

export type ClientRecord = {
  id: string;
  name: string;
  owner: string;
  /** From HubSpot: the renewal's annual value and date. */
  annualValue: number;
  renewal: string;
  /** From Xero: the unpaid balance past due, and the oldest invoice in it. */
  overdue: number;
  oldestInvoice: string | null;
  invoiceDue: string | null;
  /** From Outlook: the latest message on the account and what it says. */
  latestEmail: string;
  emailSummary: string;
  nextStep: string;
};

export const CLIENTS: readonly ClientRecord[] = [
  {
    id: "rec_cli_brightwell",
    name: "Brightwell Foods",
    owner: "Leah Carter",
    annualValue: 96_000,
    renewal: "2026-10-14",
    overdue: 18_400,
    oldestInvoice: "INV-2291",
    invoiceDue: "2026-09-03",
    latestEmail: "2026-09-02",
    emailSummary: "Their finance team asked for a line-by-line breakdown of INV-2291. No reply yet.",
    nextStep: "Send the breakdown, then agree a payment date.",
  },
  {
    id: "rec_cli_orchard",
    name: "Orchard Lane Health",
    owner: "Tom Reeve",
    annualValue: 42_000,
    renewal: "2026-09-30",
    overdue: 0,
    oldestInvoice: null,
    invoiceDue: null,
    latestEmail: "2026-09-09",
    emailSummary: "Renewal proposal sent. No reply from the client.",
    nextStep: "Call before the renewal date.",
  },
  {
    id: "rec_cli_kestrel",
    name: "Kestrel Freight",
    owner: "Ana Duarte",
    annualValue: 61_500,
    renewal: "2026-11-03",
    overdue: 4_250,
    oldestInvoice: "INV-2317",
    invoiceDue: "2026-09-16",
    latestEmail: "2026-09-21",
    emailSummary: "Asked for a revised delivery plan for the October loads.",
    nextStep: "Send the revised plan by Friday.",
  },
];

/** Whole days an invoice is past its due date on the day of the question. */
export function daysOverdue(client: ClientRecord): number | null {
  return client.invoiceDue ? daysBetween(client.invoiceDue, "2026-09-24") : null;
}

export const STATUS_OPTIONS = {
  attention: { value: "needs_attention", label: "Needs attention", color: "amber" },
  onTrack: { value: "on_track", label: "On track", color: "green" },
} as const;

// A workflow keeps every field current from HubSpot, Xero and Outlook.
const field = workflowField;

/** The Clients data set's schema, in the order the sheet lists it. */
export const CLIENT_FIELDS: WorkspaceField[] = [
  field("name", "Name", "text", { primary: true }),
  field("owner", "Account owner", "text"),
  field("status", "Status", "select", { options: [STATUS_OPTIONS.attention, STATUS_OPTIONS.onTrack] }),
  field("annual_value", "Annual value", "currency"),
  field("renewal", "Renewal date", "date"),
  field("overdue", "Overdue balance", "currency"),
  field("oldest_invoice", "Oldest unpaid invoice", "text"),
  field("days_overdue", "Days overdue", "number"),
  field("latest_email", "Latest client email", "date"),
  field("email_summary", "Email summary", "text", { display: "multiline" }),
  field("next_step", "Next step", "text", { display: "multiline" }),
];

/** A client's values, keyed by field. */
export function clientValues(client: ClientRecord): Record<string, unknown> {
  return {
    name: client.name,
    owner: client.owner,
    status: STATUS_OPTIONS.attention.value,
    annual_value: client.annualValue,
    renewal: client.renewal,
    overdue: client.overdue,
    oldest_invoice: client.oldestInvoice,
    days_overdue: daysOverdue(client),
    latest_email: client.latestEmail,
    email_summary: client.emailSummary,
    next_step: client.nextStep,
  };
}

/**
 * Each field as the record sheet prints it (formatFieldValue's text), written
 * out because the formatter reads @mlx/contracts at runtime; the test checks
 * every one against the application's formatter.
 */
export const CLIENT_DISPLAYS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  rec_cli_brightwell: {
    name: "Brightwell Foods",
    owner: "Leah Carter",
    annual_value: "£96,000",
    renewal: "14 Oct 2026",
    overdue: "£18,400",
    oldest_invoice: "INV-2291",
    days_overdue: "21",
    latest_email: "2 Sep 2026",
    email_summary: "Their finance team asked for a line-by-line breakdown of INV-2291. No reply yet.",
    next_step: "Send the breakdown, then agree a payment date.",
  },
  rec_cli_orchard: {
    name: "Orchard Lane Health",
    owner: "Tom Reeve",
    annual_value: "£42,000",
    renewal: "30 Sep 2026",
    overdue: "£0.00",
    oldest_invoice: "-",
    days_overdue: "-",
    latest_email: "9 Sep 2026",
    email_summary: "Renewal proposal sent. No reply from the client.",
    next_step: "Call before the renewal date.",
  },
  rec_cli_kestrel: {
    name: "Kestrel Freight",
    owner: "Ana Duarte",
    annual_value: "£61,500",
    renewal: "3 Nov 2026",
    overdue: "£4,250",
    oldest_invoice: "INV-2317",
    days_overdue: "8",
    latest_email: "21 Sep 2026",
    email_summary: "Asked for a revised delivery plan for the October loads.",
    next_step: "Send the revised plan by Friday.",
  },
};

/** Where the product links a record: its channel, with the record open. */
export function clientHref(client: Pick<ClientRecord, "id">): string {
  return calderRecordHref(CLIENTS_CHANNEL_ID, client.id);
}

/* ------------------------------------------------------------------ */
/*  The turn                                                            */
/* ------------------------------------------------------------------ */

export const CLIENTS_QUESTION = "Which clients need attention before month-end?";

const [BRIGHTWELL, ORCHARD, KESTREL] = CLIENTS as [ClientRecord, ClientRecord, ClientRecord];

/** The records the answer links, in reading order: the sheet's previous and next. */
export const CLIENTS_ANSWER_RECORD_IDS = [BRIGHTWELL.id, ORCHARD.id, KESTREL.id] as const;

/**
 * The answer as the run wrote it: one paragraph that says why each client
 * needs attention, then the table that puts each system's part side by side.
 * Short enough that the question, the answer and its sources fit one frame.
 */
export const CLIENTS_ANSWER_MARKDOWN = `Three clients need attention before month-end. [Brightwell Foods](${clientHref(BRIGHTWELL)}) is 21 days late paying INV-2291 (**£18,400**), but its finance team asked for a breakdown of that invoice on 2 September and hasn't had a reply. [Orchard Lane Health](${clientHref(ORCHARD)}) renews on 30 September and hasn't answered the proposal sent on the 9th. [Kestrel Freight](${clientHref(KESTREL)}) asked for a revised delivery plan on Monday and has £4,250 overdue.

| Client | Renewal · HubSpot | Overdue · Xero | Latest email · Outlook |
| --- | --- | --- | --- |
| Brightwell Foods | 14 Oct · £96,000 | £18,400 · 21 days | Invoice query, 2 Sept |
| Orchard Lane Health | 30 Sept · £42,000 | None | Proposal unanswered, 9 Sept |
| Kestrel Freight | 3 Nov · £61,500 | £4,250 · 8 days | Revised plan requested, 21 Sept |`;
